import * as React from 'react';
import {
  Drawer,
  Button,
  Form,
  Select,
  InputNumber,
  Input,
  Table,
  Space,
  Divider,
  Row,
  Col,
  message,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  UploadOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import withStyles from 'react-jss';
import { connect } from 'react-redux';
import {
  getActiveAdhocCharges,
  getConsignmentDetails,
  addAdhocCharges,
  getS3SignedUrlAdhoc,
  putImageS3,
  sendPayoutForReview,
  fetchTripReferencesForVehicle,
} from '../Receivables.api';
import moment from 'moment';
import BillingCyclePicker, { getDefaultDateRange } from './BillingCyclePicker';
import ConfirmActionModal from 'src/common/ConfirmActionModal';
import { transformName, capitalize } from 'src/utils/utils';

interface AddAdhocChargeDrawerProps {
  visible: boolean;
  onClose: () => void;
  classes?: any;
  selectedCalculation?: any;
  onSuccess?: () => void;
  billingCycleType?: 'weekly' | 'monthly';
  billingCycleStart?: number;
  isCNAggregationAtTrip?: boolean;
  isVehicleLevelAggregation?: boolean;
  activeViewType?: string;
  viewSplitConfig?: {
    resolution_key: string;
    views: Array<{
      key: string;
      value: string;
      pretty_name?: string;
    }>;
  };
}

const ENTITY_TYPE_OPTIONS = [
  { label: 'Trip', value: 'trip' },
  { label: 'Consignment', value: 'consignment' },
  { label: 'Vehicle', value: 'vehicle' },
];

const styles = () => ({
  container: {
    padding: '0 0px',
  },
  formSection: {
    borderRadius: '4px',
  },
  tableSection: {
    width: '100%',
    marginBottom: 20,
    '& .ant-table': {
      borderRadius: 4,
      border: '1px solid #f0f0f0',
    },
    '& .ant-table-tbody > tr:hover > td': {
      background: 'transparent',
    },
    '& .ant-table-thead > tr > th': {
      padding: '8px',
      background: '#f3f7fb',
    },
    '& .ant-table-tbody > tr > td': {
      padding: '8px',
    },
    '& .ant-form-item .ant-upload': {
      width: '100%',
    },
    '& .ant-form-item-explain, & .ant-form-item-extra': {
      clear: 'both',
      color: 'rgba(0, 0, 0, 0.45)',
      fontSize: '14px',
      lineHeight: 1.5715,
      transition: 'color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1)',
      minHeight: 'fit-content',
    },
    '& .ant-select:not(.ant-select-customize-input) .ant-select-selector': {
      border: '1px solid #999999 !important',
      borderRadius: '4px !important',
      height: '32px !important',
      fontSize: '14px !important',
      color: 'rgba(0, 0, 0, 0.85) !important',
    },
    '& .ant-select-selection-placeholder': {
      color: '#bfbfbf !important',
      fontSize: '14px !important',
    },
    '& .ant-select-arrow': {
      color: 'rgba(0, 0, 0, 0.25) !important',
    },
    '& .ant-input': {
      backgroundColor: '#fff !important',
      border: '1px solid #999999 !important',
      borderRadius: '4px !important',
      height: '32px !important',
      fontSize: '14px !important',
      color: 'rgba(0, 0, 0, 0.85) !important',
      padding: '4px 11px !important',
    },
    '& .ant-input::placeholder': {
      color: '#bfbfbf !important',
      fontSize: '14px !important',
    },
    '& .ant-input-number': {
      border: '1px solid #999999 !important',
      borderRadius: '4px !important',
      height: '32px !important',
      width: '100%',
    },
    '& .ant-input-number-input': {
      fontSize: '14px !important',
      height: '30px !important',
      padding: '4px 11px !important',
    },
    '& .ant-input:disabled': {
      backgroundColor: '#f5f5f5 !important',
      color: 'rgba(0, 0, 0, 0.25) !important',
    },
  },
  totalSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 20,
    padding: '16px',
    borderTop: '1px solid #f0f0f0',
    borderRadius: '0 0 4px 4px',
    background: '#ecf4f2',
    width: '100%',
  },
  totalLabel: {
    fontWeight: 'bold',
    marginRight: 10,
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    width: '100%',
    '& .anticon': {
      marginRight: 5,
    },
    '& .ant-btn': {
      padding: '0 8px',
    },
  },
  deleteIcon: {
    color: '#ff7875',
    cursor: 'pointer',
    fontSize: '18px',
  },
  readOnlyLabel: {
    fontSize: 'small',
  },
  readOnlyField: {
    fontSize: 'small',
    fontWeight: 'bold',
  },
  zeroMargin: {
    margin: 0,
  },
  zeroMarginBottom: {
    marginBottom: 0,
  },
  centerAlign: {
    display: 'flex',
    justifyContent: 'center',
  },
  searchInput: {
    width: '100%',
  },
  divider: {
    margin: '10px 0px',
  },
});

const getInitialChargeRow = () => ({
  key: Date.now() + Math.random(),
  entityLevel: undefined,
  referenceNumber: undefined,
  linkedTripRefNumber: undefined,
  chargeObj: undefined,
  amount: null,
  attachment: null,
  remarks: '',
});

const isChargeActive = (charge) => charge?.is_active;

const createChargeRowFromExisting = (charge, index, fetchedCharges = []) => {
  const foundCharge = fetchedCharges.find((fc) => fc.charge_id === charge.chargeId);

  // If charge not found, create a fallback chargeObj to display the existing data
  const chargeObj = foundCharge || {
    charge_id: charge.chargeId,
    charge_name: charge.chargeName || 'Unknown Charge',
    proof_mandatory: false,
    payoutPenalty: charge.chargeType || 'payout',
    label: charge.chargeType || 'payout',
    is_active: false,
  };

  return {
    key: Date.now() + Math.random() + index,
    entityLevel: charge.entityType,
    referenceNumber: charge.entityReferenceNumber,
    linkedTripRefNumber: charge.linkedTripReferenceNumber,
    chargeObj,
    amount: charge.payout,
    attachment: charge.attachment ? { url: charge.attachment, name: 'Attachment' } : null,
    remarks: charge.remark || '',
    disabled: !isChargeActive(foundCharge),
  };
};

const AddAdhocChargeDrawer = (props: AddAdhocChargeDrawerProps) => {
  const {
    visible,
    onClose,
    classes = {},
    selectedCalculation,
    onSuccess,
    billingCycleType,
    billingCycleStart,
    isCNAggregationAtTrip,
    isVehicleLevelAggregation,
    activeViewType,
    viewSplitConfig,
  } = props;
  const isEditable = selectedCalculation === null || selectedCalculation === undefined;
  const adhocChargesArray = (selectedCalculation?.payout || []).filter(
    (item) => item.payoutType === 'adhoc',
  );
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [chargeOptions, setChargeOptions] = React.useState([]);
  const [selectedEntityType, setSelectedEntityType] = React.useState('');
  const [chargeRows, setChargeRows] = React.useState([]);
  const [submitted, setSubmitted] = React.useState(false);
  const [isChargeFetching, setIsChargeFetching] = React.useState(false);
  const [consignmentOptions, setConsignmentOptions] = React.useState([]);
  const [uploadingFiles, setUploadingFiles] = React.useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = React.useState(false);
  const [sendForReviewConfirmVisible, setSendForReviewConfirmVisible] = React.useState(false);
  const [tripReferenceOptions, setTripReferenceOptions] = React.useState<string[]>([]);
  const [consignmentsByTrip, setConsignmentsByTrip] = React.useState<{
    [tripRef: string]: string[];
  }>({});
  const [selectedBillingCycle, setSelectedBillingCycle] = React.useState<{
    startDate: moment.Moment;
    endDate: moment.Moment;
  } | null>(null);

  const totalAmount = React.useMemo(() => {
    return chargeRows.reduce((sum, row) => sum + (row.amount || 0), 0);
  }, [chargeRows]);

  const handleDrawerClick = () => {
    if (submitted) {
      setSubmitted(false);
    }
  };

  const subEntityTypesMap = {
    vehicle: isVehicleLevelAggregation ? ['trip', 'consignment'] : [],
    trip: isCNAggregationAtTrip || isVehicleLevelAggregation ? ['consignment'] : [],
  };

  const getAvailableChargeOptions = (entityLevel, referenceNumber) => {
    const effectiveEntityType = isEditable ? selectedEntityType : selectedCalculation?.entity_type;
    if ((isCNAggregationAtTrip && effectiveEntityType === 'trip') || isVehicleLevelAggregation) {
      const usedChargePairs = chargeRows
        .filter((row) => row.chargeObj)
        .map((row) => ({
          chargeId: row.chargeObj.charge_id,
          referenceNumber: row.referenceNumber,
        }));

      const filtered = chargeOptions.filter((charge) => {
        if (entityLevel !== charge.entity_type) return false;

        return !usedChargePairs.some(
          (pair) => pair.chargeId === charge.charge_id && pair.referenceNumber === referenceNumber,
        );
      });
      return filtered;
    }

    const usedChargeIds = chargeRows
      .filter((row) => row.chargeObj)
      .map((row) => row.chargeObj.charge_id);
    return chargeOptions.filter((charge) => !usedChargeIds.includes(charge.charge_id));
  };

  const handleEntityTypeChange = async (value) => {
    setSelectedEntityType(value);
    form.setFieldsValue({
      entityType: value,
      groupReferenceNumber: undefined,
      entityReference: undefined,
      linkedTripReference: undefined,
      consignmentReference: undefined,
    });
    setChargeRows([]);
    setConsignmentOptions([]);

    if (value) {
      setIsChargeFetching(true);
      try {
        const entityTypes: string[] = [value, ...(subEntityTypesMap[value] || [])];
        const chargeResponses = await Promise.all(
          entityTypes.map((et) => getActiveAdhocCharges(et)),
        );
        const fetchedCharges = chargeResponses.flatMap((res) => res?.data?.charges || []);
        const isVehicleLevelAggregationEntity = value === 'vehicle' && isVehicleLevelAggregation;
        if (isVehicleLevelAggregationEntity) {
          if (selectedCalculation?.entity_reference_number) {
            const tripRefsResponse = await fetchTripReferencesForVehicle({
              vehicleNumber: selectedCalculation.entity_reference_number,
              fromDate: moment(selectedCalculation.start_date).format('YYYY-MM-DD'),
              toDate: moment(selectedCalculation.end_date).format('YYYY-MM-DD'),
              viewTypeKey: viewSplitConfig?.resolution_key,
              viewTypeValue: viewSplitConfig?.views?.find((view) => view.key === activeViewType)
                ?.value,
            });
            if (tripRefsResponse.isSuccess) {
              setTripReferenceOptions(tripRefsResponse?.data || []);
            } else {
              message.error(tripRefsResponse.errorMessage || t('trip_references_fetch_failed'));
              setTripReferenceOptions([]);
            }
          }
        }

        setChargeOptions(fetchedCharges.filter(isChargeActive));
        if (adhocChargesArray.length) {
          const populatedRows = adhocChargesArray.map((charge, index) =>
            createChargeRowFromExisting(charge, index, fetchedCharges));
          setChargeRows(populatedRows);
        }
      } catch (error) {
        message.error(t('error_fetching_charges') || 'Error fetching charges');
        setChargeOptions([]);
      } finally {
        setIsChargeFetching(false);
      }
    } else {
      setChargeOptions([]);
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    setLoading(true);
    try {
      if (isEditable) {
        await form.validateFields();

        if (!selectedBillingCycle) {
          message.error(t('billing_cycle_required') || 'Please select a billing cycle');
          setLoading(false);
          return false;
        }
      }

      const effectiveEntityType = isEditable
        ? selectedEntityType
        : selectedCalculation?.entity_type;
      const shouldValidateEntityLevel =
        (effectiveEntityType === 'trip' && isCNAggregationAtTrip) ||
        (effectiveEntityType === 'vehicle' && isVehicleLevelAggregation);
      const activeRows = chargeRows.filter((row) => !row.disabled);

      if (activeRows.length === 0 && (isEditable || adhocChargesArray.length === 0)) {
        message.error(t('add_at_least_one_charge') || 'Please add at least one charge');
        setLoading(false);
        return false;
      }

      const rowsMissingCharge = activeRows.filter((row) => !row.chargeObj);
      const rowsMissingAmount = activeRows.filter((row) => !row.amount && row.chargeObj);
      const rowsMissingAttachment = activeRows.filter(
        (row) => row.chargeObj?.proof_mandatory === true && !row.attachment,
      );

      let hasValidationErrors =
        rowsMissingCharge.length > 0 ||
        rowsMissingAmount.length > 0 ||
        rowsMissingAttachment.length > 0;

      if (shouldValidateEntityLevel) {
        const rowsMissingEntityLevel = activeRows.filter((row) => !row.entityLevel);
        const rowsMissingReference = activeRows.filter(
          (row) => row.entityLevel === 'consignment' && !row.referenceNumber,
        );
        const rowsMissingLinkedTrip = activeRows.filter(
          (row) =>
            effectiveEntityType === 'vehicle' &&
            row.entityLevel === 'consignment' &&
            !row.linkedTripRefNumber,
        );

        hasValidationErrors =
          hasValidationErrors ||
          rowsMissingEntityLevel.length > 0 ||
          rowsMissingReference.length > 0 ||
          rowsMissingLinkedTrip.length > 0;
      }

      if (hasValidationErrors) {
        message.error(t('fix_validation_errors') || 'Please fix validation errors');
        setLoading(false);
        return false;
      }

      // Extract common source values based on mode
      let entityReferenceNumber;
      let linkedTripReferenceNumber;
      const parentEntityType = isEditable ? selectedEntityType : selectedCalculation?.entity_type;
      const isVehicleLevelAggregationEntity =
        isVehicleLevelAggregation && parentEntityType === 'vehicle';
      const isTripAggr =
        (isCNAggregationAtTrip || isVehicleLevelAggregation) && parentEntityType === 'trip';

      if (isEditable) {
        const isConsignment = selectedEntityType === 'consignment';
        entityReferenceNumber = form.getFieldValue(
          isConsignment ? 'consignmentReference' : 'entityReference',
        );
        linkedTripReferenceNumber = isConsignment
          ? form.getFieldValue('linkedTripReference')
          : null;
      } else {
        entityReferenceNumber = selectedCalculation?.entity_reference_number;
        const tripRefMap = {
          consignment: selectedCalculation?.linked_trip_reference_number,
          trip: selectedCalculation?.entity_reference_number,
        };
        linkedTripReferenceNumber = tripRefMap[parentEntityType] || null;
      }

      const billingCycle = isEditable ? selectedBillingCycle : null;

      const payload = {
        ...(isEditable
          ? {}
          : {
              payout_id: selectedCalculation?.id?.toString(),
              carrier_id: selectedCalculation?.object_id,
              charges_version: selectedCalculation?.adhoc_charges_version,
            }),
        entity_type: parentEntityType,
        entity_reference_number: entityReferenceNumber,
        linked_trip_reference_number: linkedTripReferenceNumber,
        start_date: isEditable
          ? billingCycle?.startDate.format('YYYY-MM-DD')
          : selectedCalculation?.start_date,
        end_date: isEditable
          ? billingCycle?.endDate.format('YYYY-MM-DD')
          : selectedCalculation?.end_date,
        charges: chargeRows.map((row) => {
          const entityLevel = row.entityLevel || parentEntityType;
          const isSubEntity = entityLevel === 'trip' || entityLevel === 'consignment';

          const getLinkedTripRef = () => {
            if (entityLevel !== 'consignment') return undefined;
            if (isVehicleLevelAggregationEntity) return row.linkedTripRefNumber;
            if (isTripAggr) return entityReferenceNumber;
            return linkedTripReferenceNumber;
          };

          const linkedVehicleRef =
            isVehicleLevelAggregationEntity && isSubEntity ? entityReferenceNumber : undefined;

          return {
            charge_id: row.chargeObj?.charge_id,
            charge_name: row.chargeObj?.charge_name,
            charge_amount: row.amount,
            charge_type: row.chargeObj?.payoutPenalty || row.chargeObj?.label || 'payout',
            remarks: row.remarks,
            attachment: row.attachment?.url,
            entity_level: entityLevel,
            reference_number: row.referenceNumber || entityReferenceNumber,
            linked_trip_reference_number: getLinkedTripRef(),
            linked_vehicle_reference_number: linkedVehicleRef,
          };
        }),
      };

      const response = await addAdhocCharges(payload, activeViewType);

      if (response.isSuccess) {
        message.success(t('adhoc_charge_added_success') || 'Adhoc charge added successfully');
        setSubmitted(false);
        setLoading(false);
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else if (response.errCode === 'STALE_CHARGES') {
        message.error(t(response.errorMessage || 'adhoc_charges_stale'));
        setLoading(false);
        onClose();
        return false;
      } else {
        message.error(response.errorMessage || 'Failed to add adhoc charge');
        setLoading(false);
        return false;
      }

      return true;
    } catch (error) {
      setLoading(false);
      return false;
    }
  };

  const handleSendForReview = async () => {
    const payoutId = selectedCalculation.id;
    if (!payoutId) {
      message.error('Payout ID not found');
      return;
    }

    const response = await sendPayoutForReview(String(payoutId), activeViewType);
    if (response.data?.success) {
      message.success('Successfully sent for review');
      onClose();
    } else {
      message.error(
        response.errorMessage ||
          response.data?.failures?.[0]?.message ||
          t('send_for_review_failed'),
      );
    }
  };

  const handleTripReferenceChange = async (referenceNumber) => {
    if (referenceNumber) {
      try {
        if (isEditable && selectedEntityType === 'consignment') {
          form.setFieldsValue({ consignmentReference: undefined });
        }

        const result = await getConsignmentDetails(referenceNumber);
        if (result.isSuccess) {
          const consignments = result.data || [];

          if (
            (isCNAggregationAtTrip && selectedCalculation?.entity_type === 'trip') ||
            isEditable
          ) {
            setConsignmentOptions(consignments);
          }
        } else {
          message.error(result.errorMessage || 'Failed to fetch trip details');
          setConsignmentOptions([]);
        }
      } catch (error) {
        message.error(t('trip_details_fetch_failed') || 'Error fetching trip details');
        setConsignmentOptions([]);
      }
    } else {
      setConsignmentOptions([]);
    }
  };

  const fetchConsignmentsForTrip = async (tripRef: string) => {
    if (!tripRef || consignmentsByTrip[tripRef]) return;
    const result = await getConsignmentDetails(tripRef);
    if (!result.isSuccess) {
      message.error(result.errorMessage || 'Failed to fetch consignments');
      return;
    }
    setConsignmentsByTrip((prev) => ({
      ...prev,
      [tripRef]: result.data || [],
    }));
  };

  const entityRefDebounceTimer = React.useRef<any>(null);

  const fetchEntityReferenceData = async (value, billingCycleOverride?) => {
    if (selectedEntityType === 'vehicle' && isVehicleLevelAggregation) {
      const cycle = billingCycleOverride || selectedBillingCycle;
      const tripRefsResponse = await fetchTripReferencesForVehicle({
        vehicleNumber: value,
        fromDate: cycle.startDate.format('YYYY-MM-DD'),
        toDate: cycle.endDate.format('YYYY-MM-DD'),
        viewTypeKey: viewSplitConfig?.resolution_key,
        viewTypeValue: viewSplitConfig?.views?.find((view) => view.key === activeViewType)?.value,
      });
      if (tripRefsResponse.isSuccess) {
        setTripReferenceOptions(tripRefsResponse?.data || []);
      } else {
        message.error(tripRefsResponse.errorMessage || t('trip_references_fetch_failed'));
        setTripReferenceOptions([]);
      }
    } else if (selectedEntityType === 'trip') {
      await handleTripReferenceChange(value);
    }
  };

  const handleEditableEntityReferenceChange = (value) => {
    if (!value || !isEditable) return;
    if (entityRefDebounceTimer.current) {
      clearTimeout(entityRefDebounceTimer.current);
    }
    entityRefDebounceTimer.current = setTimeout(() => {
      fetchEntityReferenceData(value);
    }, 500);
  };

  React.useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedEntityType('');
      setChargeOptions([]);
      setChargeRows([]);
      setConsignmentOptions([]);
      setTripReferenceOptions([]);
      setConsignmentsByTrip({});
      setSelectedBillingCycle(getDefaultDateRange(billingCycleType, billingCycleStart));
      setSubmitted(false);

      if (selectedCalculation) {
        handleEntityTypeChange(selectedCalculation.entity_type);
        if (
          isCNAggregationAtTrip &&
          selectedCalculation.entity_type === 'trip' &&
          selectedCalculation.entity_reference_number
        ) {
          handleTripReferenceChange(selectedCalculation.entity_reference_number);
        }
        if (adhocChargesArray.length) {
          const populatedRows = adhocChargesArray.map((charge, index) =>
            createChargeRowFromExisting(charge, index));
          setChargeRows(populatedRows);
        }
      }
    }
  }, [visible]);

  const addChargeRow = () => {
    if (!selectedEntityType && !selectedCalculation) {
      message.warning(t('select_entity_type_first') || 'Please select entity type first');
      return;
    }

    const addableRowCount = chargeRows.filter((row) => !row.disabled).length;

    if (!isCNAggregationAtTrip && addableRowCount >= chargeOptions.length) {
      message.warning(t('no_more_charges') || 'No more charges available to add');
      return;
    }

    setChargeRows((prevChargeRows) => [...prevChargeRows, getInitialChargeRow()]);
  };

  const removeChargeRow = (key) => {
    setChargeRows((prevChargeRows) => prevChargeRows.filter((row) => row.key !== key));
  };

  const getSignedAmount = (amount, chargeObj) => {
    if (amount === null || !chargeObj) return amount;
    const isPayout =
      chargeObj.payoutPenalty?.toLowerCase().includes('payout') ||
      chargeObj.label?.toLowerCase().includes('payout');
    return isPayout ? Math.abs(amount) : -Math.abs(amount);
  };

  const updateChargeRow = (key, field, value) => {
    setChargeRows((prevChargeRows) =>
      prevChargeRows.map((row) => {
        if (row.key === key) {
          if (field === 'chargeObj') {
            return {
              ...row,
              chargeObj: value,
              amount: getSignedAmount(row.amount, value),
            };
          }

          if (field === 'amount') {
            return { ...row, amount: getSignedAmount(value, row.chargeObj) };
          }

          const updatedRow = { ...row, [field]: value };

          return updatedRow;
        }

        return row;
      }));
  };

  const columns = React.useMemo(() => {
    const entityType = isEditable ? selectedEntityType : selectedCalculation?.entity_type;
    const isVehicleLevel = entityType === 'vehicle';
    const showEntityLevelColumns =
      (isCNAggregationAtTrip && entityType === 'trip') ||
      (isVehicleLevelAggregation && entityType !== 'consignment');
    return [
      ...(showEntityLevelColumns
        ? [
            {
              title: 'Entity Level',
              dataIndex: 'entityLevel',
              key: 'entityLevel',
              width: '10%',
              render: (entityLevel, record) => {
                const showError = submitted && !record.disabled && !entityLevel;

                return (
                  <Form.Item
                    className={classes.zeroMargin}
                    help={showError ? t('entity_level_required') : ''}
                    validateStatus={showError ? 'error' : ''}
                  >
                    <Select
                      placeholder={t('select_level')}
                      value={entityLevel}
                      onChange={(value) => {
                        updateChargeRow(record.key, 'entityLevel', value);
                        updateChargeRow(record.key, 'chargeObj', undefined);
                        updateChargeRow(record.key, 'linkedTripRefNumber', undefined);
                        const parentEntityReference = isEditable
                          ? form.getFieldValue('entityReference')
                          : selectedCalculation?.entity_reference_number;
                        const autoFill =
                          value === 'vehicle' || (value === 'trip' && entityType === 'trip');
                        updateChargeRow(
                          record.key,
                          'referenceNumber',
                          autoFill ? parentEntityReference : undefined,
                        );
                        if (value === 'consignment' && entityType === 'trip') {
                          updateChargeRow(record.key, 'linkedTripRefNumber', parentEntityReference);
                        }
                      }}
                      disabled={record.disabled}
                    >
                      {[
                        isVehicleLevel && 'vehicle',
                        (entityType === 'trip' || isVehicleLevel) && 'trip',
                        'consignment',
                      ]
                        .filter(Boolean)
                        .map((level) => (
                          <Select.Option key={level} value={level}>
                            {t(level)}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                );
              },
            },
            ...(isVehicleLevel && isVehicleLevelAggregation
              ? [
                  {
                    title: 'Linked Trip Reference',
                    dataIndex: 'linkedTripRefNumber',
                    key: 'linkedTripRefNumber',
                    width: '15%',
                    render: (linkedTripRefNumber, record) => {
                      const isConsignment = record.entityLevel === 'consignment';
                      const showError =
                        submitted && !record.disabled && isConsignment && !linkedTripRefNumber;

                      return (
                        <Form.Item
                          className={classes.zeroMargin}
                          help={
                            showError ? t('linked_trip_required') || 'Linked trip is required' : ''
                          }
                          validateStatus={showError ? 'error' : ''}
                        >
                          {isConsignment ? (
                            <Select
                              placeholder={t('select_trip')}
                              value={linkedTripRefNumber}
                              onChange={(value) => {
                                updateChargeRow(record.key, 'linkedTripRefNumber', value);
                                updateChargeRow(record.key, 'referenceNumber', undefined);
                                updateChargeRow(record.key, 'chargeObj', undefined);
                                fetchConsignmentsForTrip(value);
                              }}
                              disabled={record.disabled}
                              showSearch
                            >
                              {tripReferenceOptions.map((trip) => (
                                <Select.Option key={trip} value={trip}>
                                  {trip}
                                </Select.Option>
                              ))}
                            </Select>
                          ) : (
                            <Input placeholder="-" disabled />
                          )}
                        </Form.Item>
                      );
                    },
                  },
                ]
              : []),
            {
              title: 'Reference Number',
              dataIndex: 'referenceNumber',
              key: 'referenceNumber',
              width: '15%',
              render: (referenceNumber, record) => {
                const showError =
                  submitted &&
                  !record.disabled &&
                  record.entityLevel === 'consignment' &&
                  !referenceNumber;

                return (
                  <Form.Item
                    className={classes.zeroMargin}
                    help={showError ? t('reference_number_required') : ''}
                    validateStatus={showError ? 'error' : ''}
                  >
                    {(() => {
                      if (
                        record.entityLevel === 'vehicle' ||
                        (record.entityLevel === 'trip' && !isVehicleLevel)
                      ) {
                        return (
                          <Input
                            placeholder={t(
                              record.entityLevel === 'vehicle'
                                ? 'vehicle_reference'
                                : 'trip_reference',
                            )}
                            value={
                              isEditable
                                ? form.getFieldValue('entityReference')
                                : selectedCalculation?.entity_reference_number
                            }
                            disabled
                          />
                        );
                      }
                      if (record.entityLevel === 'trip' && isVehicleLevel) {
                        return (
                          <Select
                            placeholder={t('select_trip')}
                            value={referenceNumber}
                            onChange={(value) => {
                              updateChargeRow(record.key, 'referenceNumber', value);
                              updateChargeRow(record.key, 'chargeObj', undefined);
                            }}
                            disabled={record.disabled}
                            showSearch
                          >
                            {tripReferenceOptions.map((trip) => (
                              <Select.Option key={trip} value={trip}>
                                {trip}
                              </Select.Option>
                            ))}
                          </Select>
                        );
                      }
                      if (record.entityLevel === 'consignment') {
                        const cnOptions = isVehicleLevel
                          ? consignmentsByTrip[record.linkedTripRefNumber] || []
                          : consignmentOptions;
                        return (
                          <Select
                            placeholder={t('select_cn')}
                            value={referenceNumber}
                            onChange={(value) => {
                              updateChargeRow(record.key, 'referenceNumber', value);
                              updateChargeRow(record.key, 'chargeObj', undefined);
                            }}
                            disabled={record.disabled}
                          >
                            {cnOptions.map((cn) => (
                              <Select.Option key={cn} value={cn}>
                                {cn}
                              </Select.Option>
                            ))}
                          </Select>
                        );
                      }
                      return <Input placeholder={t('select_entity_level_first')} disabled />;
                    })()}
                  </Form.Item>
                );
              },
            },
          ]
        : []),
      {
        title: 'Charge Name',
        dataIndex: 'chargeObj',
        key: 'chargeObj',
        width: showEntityLevelColumns ? '20%' : '25%',
        render: (chargeObj, record) => {
          const showError = submitted && !record.disabled && !chargeObj;
          let referenceNumber;
          if (showEntityLevelColumns) {
            const parentRef = isEditable
              ? form.getFieldValue('entityReference')
              : selectedCalculation?.entity_reference_number;
            referenceNumber =
              record.entityLevel === 'trip' ? parentRef || undefined : record.referenceNumber;
          }

          const availableCharges = getAvailableChargeOptions(record.entityLevel, referenceNumber);

          const groupedCharges = availableCharges.reduce((acc, charge) => {
            const groupKey =
              charge.payoutPenalty?.toLowerCase() || charge.label?.toLowerCase() || 'other';
            if (!acc[groupKey]) {
              acc[groupKey] = [];
            }
            acc[groupKey].push(charge);
            return acc;
          }, {});

          return (
            <Form.Item
              className={classes.zeroMargin}
              help={showError ? t('charge_name_required') : ''}
              validateStatus={showError ? 'error' : ''}
            >
              <Select
                placeholder={t('enter')}
                value={chargeObj?.charge_name}
                onChange={(value) => {
                  const selectedCharge = chargeOptions.find((charge) => charge.charge_id === value);
                  updateChargeRow(record.key, 'chargeObj', selectedCharge);
                }}
                className={classes.searchInput}
                disabled={
                  record.disabled || (showEntityLevelColumns ? !record.referenceNumber : false)
                }
                showSearch
                style={{
                  backgroundColor: record.disabled ? '#f5f5f5' : undefined,
                  cursor: record.disabled ? 'not-allowed' : undefined,
                  color: record.disabled ? 'rgba(0, 0, 0, 0.25)' : undefined,
                  borderColor: record.disabled ? '#d9d9d9' : undefined,
                  pointerEvents: record.disabled ? 'none' : undefined,
                }}
              >
                {groupedCharges['payout'] && groupedCharges['payout'].length > 0 && (
                  <Select.OptGroup label={t('payout_charges')}>
                    {groupedCharges['payout'].map((charge) => (
                      <Select.Option key={charge.charge_id} value={charge.charge_id}>
                        {charge.charge_name}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                )}

                {groupedCharges['penalty'] && groupedCharges['penalty'].length > 0 && (
                  <Select.OptGroup label={t('penalty_charges')}>
                    {groupedCharges['penalty'].map((charge) => (
                      <Select.Option key={charge.charge_id} value={charge.charge_id}>
                        {charge.charge_name}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                )}

                {Object.keys(groupedCharges)
                  .filter((key) => !['payout', 'penalty'].includes(key))
                  .map(
                    (groupKey) =>
                      groupedCharges[groupKey].length > 0 && (
                        <Select.OptGroup
                          key={groupKey}
                          label={`${groupKey.charAt(0).toUpperCase() + groupKey.slice(1)} ${t(
                            'Charges',
                          )}`}
                        >
                          {groupedCharges[groupKey].map((charge) => (
                            <Select.Option key={charge.charge_id} value={charge.charge_id}>
                              {charge.charge_name}
                            </Select.Option>
                          ))}
                        </Select.OptGroup>
                      ),
                  )}
              </Select>
            </Form.Item>
          );
        },
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: '10%',
        render: (text, record) => {
          const isPenalty =
            record.chargeObj?.payoutPenalty?.toLowerCase().includes('penalty') ||
            record.chargeObj?.label?.toLowerCase().includes('penalty');
          const absoluteValue = text !== null ? Math.abs(text) : null;
          const invalidAmount = record.chargeObj && absoluteValue === null;

          const showError =
            submitted && !record.disabled && (text === null || (record.chargeObj && invalidAmount));
          const errorMessage = t('amount_required');
          let amountColor;
          if (record.chargeObj) {
            amountColor = isPenalty ? 'rgb(245, 34, 45)' : 'rgb(82, 196, 26)';
          }

          let inputBorderColor;
          if (record.disabled) {
            inputBorderColor = '#d9d9d9 !important';
          } else if (showError) {
            inputBorderColor = '#ff4d4f !important';
          }
          const inputTextColor = amountColor;

          return (
            <Form.Item
              className={classes.zeroMargin}
              help={showError ? errorMessage : ''}
              validateStatus={showError ? 'error' : ''}
            >
              <InputNumber
                style={
                  {
                    borderColor: inputBorderColor,
                    color: inputTextColor,
                    backgroundColor: record.disabled ? '#f5f5f5 !important' : undefined,
                    cursor: record.disabled ? 'not-allowed !important' : undefined,
                    pointerEvents: record.disabled ? 'none' : undefined,
                    opacity: record.disabled ? '0.6 !important' : undefined,
                  } as React.CSSProperties
                }
                placeholder="0"
                value={absoluteValue}
                onChange={(value) => {
                  let signedValue = null;
                  if (value !== null) {
                    signedValue = isPenalty ? -Math.abs(value) : +Math.abs(value);
                  }
                  updateChargeRow(record.key, 'amount', signedValue);
                }}
                min={0}
                formatter={(value) => `${value}`}
                parser={(value) => (value ? Math.abs(parseFloat(value)) : null)}
                prefix={isPenalty ? '-' : null}
                disabled={record.disabled}
              />
            </Form.Item>
          );
        },
      },
      {
        title: 'Attachment',
        dataIndex: 'attachment',
        key: 'attachment',
        width: showEntityLevelColumns ? '15%' : '25%',
        render: (text, record) => {
          const showError =
            submitted &&
            !record.disabled &&
            record.chargeObj?.proof_mandatory &&
            !record.attachment;

          return (
            <Form.Item
              className={classes.zeroMargin}
              help={showError ? t('attachment_mandatory') : ''}
              validateStatus={showError ? 'error' : ''}
            >
              <div className={classes.fileInfo}>
                <Upload
                  accept=".pdf,application/pdf"
                  beforeUpload={async (file) => {
                    if (!record.chargeObj?.charge_id) {
                      message.error('Please select a charge first');
                      return false;
                    }

                    // Validate file type
                    const isPDF =
                      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
                    if (!isPDF) {
                      message.error('Only PDF files are allowed');
                      return false;
                    }

                    try {
                      setUploadingFiles((prev) => ({ ...prev, [record.key]: true }));

                      // Phase 2 API: Get S3 signed URL
                      const urlResponse = await getS3SignedUrlAdhoc(record.chargeObj.charge_id);

                      if (!urlResponse.isSuccess) {
                        message.error('Failed to get upload URL');
                        return false;
                      }

                      const { imageUrl, imageSignedUrl } = urlResponse.data;

                      // Upload file to S3
                      await putImageS3(imageSignedUrl, file, file.type || 'application/pdf');

                      updateChargeRow(record.key, 'attachment', {
                        url: imageUrl,
                        name: file.name,
                      });

                      message.success('File uploaded successfully');
                    } catch (error) {
                      message.error('Failed to upload file');
                    } finally {
                      setUploadingFiles((prev) => ({ ...prev, [record.key]: false }));
                    }

                    return false;
                  }}
                  fileList={
                    record.attachment
                      ? [
                          {
                            uid: '-1',
                            name: record.attachment.name,
                            status: 'done',
                            url: record.attachment.url,
                          },
                        ]
                      : []
                  }
                  onRemove={() => {
                    updateChargeRow(record.key, 'attachment', null);
                  }}
                  maxCount={1}
                  showUploadList={{
                    showRemoveIcon: !record.disabled,
                    removeIcon: <CloseOutlined style={{ color: '#ff4d4f' }} />,
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={uploadingFiles[record.key]}
                    disabled={record.disabled}
                    style={{
                      color: record.chargeObj?.proof_mandatory ? '#ff4d4f' : '#1890ff',
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      padding: '4px 15px',
                      height: '32px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      border: `1px solid ${
                        record.chargeObj?.proof_mandatory ? '#ff4d4f' : '#1659CB'
                      }`,
                      borderRadius: '4px',
                    }}
                  >
                    {record.attachment ? 'Replace' : 'Upload'}
                  </Button>
                </Upload>
              </div>
            </Form.Item>
          );
        },
      },
      {
        title: 'Remarks',
        dataIndex: 'remarks',
        key: 'remarks',
        width: showEntityLevelColumns ? '15%' : '25%',
        render: (text, record) => (
          <Input
            placeholder={t('optional_remarks')}
            value={text}
            onChange={(e) => updateChargeRow(record.key, 'remarks', e.target.value)}
            disabled={record.disabled}
          />
        ),
      },
      {
        title: () => (
          <div className={classes.centerAlign}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => addChargeRow()}
              size="small"
              loading={isChargeFetching}
              style={{ borderRadius: '4px', background: '#1659CB', color: '#FFFFFF' }}
            >
              {t('charge')}
            </Button>
          </div>
        ),
        key: 'action',
        width: '10%',
        render: (_, record) => (
          <div className={classes.centerAlign}>
            <MinusCircleOutlined
              className={classes.deleteIcon}
              style={{
                color: record.disabled ? '#d9d9d9' : '#ff7875',
                cursor: record.disabled ? 'not-allowed' : 'pointer',
                opacity: record.disabled ? 0.5 : 1,
              }}
              onClick={() => !record.disabled && removeChargeRow(record.key)}
            />
          </div>
        ),
      },
    ];
  }, [
    isCNAggregationAtTrip,
    selectedCalculation,
    chargeOptions,
    consignmentOptions,
    tripReferenceOptions,
    consignmentsByTrip,
    submitted,
    isChargeFetching,
    chargeRows,
    selectedEntityType,
    uploadingFiles,
  ]);

  const billingCycleFilterLabelStyle: React.CSSProperties = {
    backgroundColor: '#fafafa',
    border: '1px solid #999999',
    borderRight: 'none',
    borderRadius: '4px 0 0 4px',
    padding: '4px 12px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    fontFamily: "'Open Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 600,
    color: '#111111',
    whiteSpace: 'nowrap',
  };

  const renderBillingCycleField = () => {
    const showError = submitted && !selectedBillingCycle && isEditable;

    return (
      <Col flex="1">
        <div style={{ marginBottom: 4 }}>
          <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.85)' }}>
            {t('billing_cycle') || 'Billing Cycle'}
            <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={billingCycleFilterLabelStyle}>{t('billing_cycle') || 'Billing Cycle'}</span>
          <BillingCyclePicker
            cycleType={billingCycleType}
            cycleStart={billingCycleStart}
            onChange={(dateRange) => {
              setSelectedBillingCycle(dateRange);
              if (selectedEntityType === 'vehicle' && isVehicleLevelAggregation && dateRange) {
                const vehicleRef = form.getFieldValue('entityReference');
                if (vehicleRef) {
                  fetchEntityReferenceData(vehicleRef, dateRange);
                }
              }
            }}
            minWidth="200px"
            value={selectedBillingCycle}
          />
        </div>
        {showError && (
          <div style={{ color: '#ff4d4f', fontSize: '14px', marginTop: 4 }}>
            {t('billing_cycle_required') || 'Please select a billing cycle'}
          </div>
        )}
      </Col>
    );
  };

  const getEmptyTableMessage = () => {
    if (
      !isEditable &&
      selectedCalculation?.entity_type === 'vehicle' &&
      isVehicleLevelAggregation
    ) {
      return 'Click Add Charge button to add charges at vehicle, trip, or consignment level';
    }
    if (!isEditable && selectedCalculation?.entity_type === 'trip' && isCNAggregationAtTrip) {
      return 'Click Add Charge button to add charges at trip or consignment level';
    }
    if (!isEditable) {
      return 'Click Add Charge button to add charges';
    }
    if (!selectedEntityType) {
      return t('select_entity_type') || 'Please select entity type to add charges';
    }
    return t('click_add_charge') || 'Click Add Charge button to add charges';
  };

  const renderFormFields = () => {
    // For Phase 2, we always have selectedCalculation (read-only mode)
    if (!isEditable) {
      return (
        <Row gutter={10} wrap={false}>
          <Col span={6}>
            <div className={classes.readOnlyLabel}>Billing Cycle</div>
            <div className={classes.readOnlyField}>
              {selectedCalculation?.start_date && selectedCalculation?.end_date
                ? `${moment(selectedCalculation.start_date)
                    .locale('en')
                    .format('DD MMM YYYY')} - ${moment(selectedCalculation.end_date)
                    .locale('en')
                    .format('DD MMM YYYY')}`
                : 'N/A'}
            </div>
          </Col>
          <Col span={4}>
            <div className={classes.readOnlyLabel}>Entity Type</div>
            <div className={classes.readOnlyField}>
              {transformName(selectedCalculation?.entity_type || 'N/A')}
            </div>
          </Col>
          <Col span={4}>
            <div className={classes.readOnlyLabel}>Entity Reference</div>
            <div className={classes.readOnlyField}>
              {selectedCalculation?.entity_reference_number || 'N/A'}
            </div>
          </Col>
          {selectedCalculation?.entity_type === 'consignment' && (
            <Col span={4}>
              <div className={classes.readOnlyLabel}>Trip Ref Number</div>
              <div className={classes.readOnlyField}>
                {selectedCalculation?.linked_trip_reference_number || 'N/A'}
              </div>
            </Col>
          )}
        </Row>
      );
    }

    // For Phase 1 (new adhoc charge), show editable form fields
    // Scenario 1: Trip or Vehicle - Billing Cycle + Entity Type + Entity Reference
    if (selectedEntityType === 'trip' || selectedEntityType === 'vehicle') {
      return (
        <Row gutter={16} wrap={false}>
          {renderBillingCycleField()}
          <Col flex="1">
            <Form.Item
              name="entityType"
              label={t('entity_type') || 'Entity Type'}
              rules={[
                {
                  required: true,
                  message: t('entity_type_required') || 'Entity type is required',
                },
              ]}
              className={classes.zeroMarginBottom}
            >
              <Select
                placeholder={t('Enter') || 'Enter'}
                options={ENTITY_TYPE_OPTIONS}
                className={classes.searchInput}
                onChange={handleEntityTypeChange}
                value={selectedEntityType}
              />
            </Form.Item>
          </Col>
          <Col flex="1">
            <Form.Item
              name="entityReference"
              label={t('entity_reference') || 'Entity Reference'}
              rules={[
                {
                  required: true,
                  message: t('entity_reference_required') || 'Entity reference is required',
                },
              ]}
              className={classes.zeroMarginBottom}
            >
              <Input
                placeholder={t('enter_entity_reference') || 'Enter Entity Reference'}
                className={classes.searchInput}
                onChange={(e) => handleEditableEntityReferenceChange(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      );
    }

    // Scenario 2: Consignment - Billing Cycle + Entity Type + Linked Trip + Consignment
    if (selectedEntityType === 'consignment') {
      return (
        <Row gutter={16} wrap={false}>
          {renderBillingCycleField()}
          <Col flex="1">
            <Form.Item
              name="entityType"
              label={t('entity_type') || 'Entity Type'}
              rules={[
                {
                  required: true,
                  message: t('entity_type_required') || 'Entity type is required',
                },
              ]}
              className={classes.zeroMarginBottom}
            >
              <Select
                placeholder={t('Enter') || 'Enter'}
                options={ENTITY_TYPE_OPTIONS}
                className={classes.searchInput}
                onChange={handleEntityTypeChange}
                value={selectedEntityType}
              />
            </Form.Item>
          </Col>
          <Col flex="1">
            <Form.Item
              name="linkedTripReference"
              label={t('linked_trip_reference') || 'Linked Trip Reference'}
              rules={[
                {
                  required: true,
                  message:
                    t('linked_trip_reference_required') || 'Linked trip reference is required',
                },
              ]}
              className={classes.zeroMarginBottom}
            >
              <Input
                placeholder={t('enter_trip_reference') || 'Enter Trip Reference'}
                className={classes.searchInput}
                onChange={(e) => handleTripReferenceChange(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col flex="1">
            <Form.Item
              name="consignmentReference"
              label={t('consignment_reference') || 'Consignment Reference'}
              rules={[
                {
                  required: true,
                  message:
                    t('consignment_reference_required') || 'Consignment reference is required',
                },
              ]}
              className={classes.zeroMarginBottom}
            >
              <Select
                placeholder={t('select_consignment') || 'Select Consignment'}
                className={classes.searchInput}
                disabled={consignmentOptions.length === 0}
              >
                {consignmentOptions.map((cn) => (
                  <Select.Option key={cn} value={cn}>
                    {cn}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      );
    }

    // Scenario 3: No entity type selected yet - Billing Cycle + Entity Type
    return (
      <Row gutter={16} wrap={false}>
        {renderBillingCycleField()}
        <Col flex="1">
          <Form.Item
            name="entityType"
            label={t('entity_type') || 'Entity Type'}
            rules={[
              {
                required: true,
                message: t('entity_type_required') || 'Entity type is required',
              },
            ]}
            className={classes.zeroMarginBottom}
          >
            <Select
              placeholder={t('select_entity_type') || 'Select Entity Type'}
              options={ENTITY_TYPE_OPTIONS}
              className={classes.searchInput}
              onChange={handleEntityTypeChange}
              value={selectedEntityType}
            />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      width="80%"
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              style={{ padding: 0, height: 'auto', marginRight: '8px' }}
            />
            <span>Add Adhoc Charge</span>
          </div>
          <Space>
            <Button style={{ border: 'none' }} onClick={onClose}>
              {t('Cancel') || 'Cancel'}
            </Button>
            <Button
              style={{ borderRadius: '4px', background: '#1659CB', color: '#FFFFFF' }}
              type="primary"
              onClick={handleSubmit}
              loading={loading}
            >
              {t('save') || 'Save'}
            </Button>
            {!isEditable && (
              <Button
                style={{ borderRadius: '4px', background: '#1659CB', color: '#FFFFFF' }}
                type="primary"
                onClick={() => setSendForReviewConfirmVisible(true)}
              >
                Send for Review
              </Button>
            )}
          </Space>
        </div>
      }
      headerStyle={{ padding: '10px 16px', borderBottom: '1px solid #969696' }}
      bodyStyle={{ padding: '15px' }}
      closable={false}
    >
      <div className={classes.container} onClick={handleDrawerClick} onKeyPress={handleDrawerClick}>
        <div className={classes.formSection}>
          <Form form={form} layout="vertical">
            {renderFormFields()}
          </Form>
        </div>

        <Divider className={classes.divider} />

        <div className={classes.tableSection}>
          <Table
            columns={columns}
            dataSource={chargeRows}
            pagination={false}
            rowKey="key"
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: getEmptyTableMessage(),
            }}
          />
        </div>

        <div className={classes.totalSection}>
          <span className={classes.totalLabel}>Total: {totalAmount.toFixed(2)}</span>
        </div>
      </div>
      <ConfirmActionModal
        visible={sendForReviewConfirmVisible}
        onCancel={() => setSendForReviewConfirmVisible(false)}
        onConfirm={() => {
          setSendForReviewConfirmVisible(false);
          handleSendForReview();
        }}
        message={
          'You are about to submit ' +
          `${capitalize(selectedCalculation?.entity_type || '')} ` +
          `${selectedCalculation?.entity_reference_number || ''} ` +
          'for review'
        }
      />
    </Drawer>
  );
};

const mapStateToProps = (state: any) => ({
  billingCycleType: state.receivables?.billingCycleType,
  billingCycleStart: state.receivables?.billingCycleStart,
  isCNAggregationAtTrip: state.receivables?.isCnAggregationAtTrip,
  isVehicleLevelAggregation: state.receivables?.isVehicleLevelAggregation,
  activeViewType: state.receivables?.activeViewType,
  viewSplitConfig: state.receivables?.viewSplitConfig,
});

export default connect(mapStateToProps)(
  withStyles(styles, { injectTheme: true })(AddAdhocChargeDrawer),
);
